import { useState } from "react";
import { Button, Card, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { faMultiply, faPen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { createFeature } from "../../api/offerings/features/createFeature";
import { deleteFeature } from "../../api/offerings/features/deleteFeature";
import { updateFeature } from "../../api/offerings/features/editFeature";
import { getFeatures } from "../../api/offerings/features/getFeatures";
import { FieldType, modalActions } from "../../constants";
import { FeatureDto } from "../../types/dto";
import { BlockInput } from "../blockInput";
import { DataFormModal } from "../dataForm/dataFormModal";

export const FeatureRow = ({
	feature,
	refreshFeatures,
}: {
	feature: FeatureDto;
	refreshFeatures: () => void;
}) => {
	const { id, name, code, description } = feature;
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const { mutateAsync: editFeatureM } = useMutation({
		mutationFn: updateFeature,
		onSettled: () => refreshFeatures(),
	});

	const { mutateAsync: deleteFeatureM } = useMutation({
		mutationFn: deleteFeature,
		onSettled: () => refreshFeatures(),
	});

	const editFeatureSetting = {
		action: modalActions.EDIT,
		fields: [
			[
				{
					field: "code",
					isMandatory: true,
					title: "Feature Code",
					placeholder: "Enter Feature Code",
				},
			],
			[
				{
					field: "name",
					isMandatory: true,
					title: "Name",
					placeholder: "Enter Name",
				},
			],
			[
				{
					field: "description",
					title: "Description",
					type: FieldType.TEXT_AREA,
					placeholder: "Enter Description",
				},
			],
			[
				{
					field: "defaultValue",
					title: "Default Value (On / Off)",
					type: FieldType.CHECKBOX,
				},
			],
		],
		buttonTitle: "Edit Feature",
		formatModalTitle: () => `Edit Feature`,
		submitButtonText: "Edit Feature",
		apiFunctionSubmit: (data) => editFeatureM({ ...data, featureId: id }),
	};

	const deleteFeatureSetting = {
		action: modalActions.DELETE,
		buttonTitle: "Delete Feature",
		formatModalTitle: () => `Delete Feature`,
		formatBodyMessage: (feature: FeatureDto) =>
			`Are you sure you wish to delete feature - ${feature.code}: ${feature.name}`,
		submitButtonText: "Delete Feature",
		apiFunctionSubmit: () => deleteFeatureM({ featureId: id }),
	};

	return (
		<>
			<DataFormModal
				isModalOpen={isModalOpen}
				setIsModalOpen={setIsModalOpen}
				initialModalData={[feature]}
				editorSetting={editFeatureSetting}
				isAutoOpen={false}
			/>
			<DataFormModal
				isModalOpen={isDeleteModalOpen}
				setIsModalOpen={setIsDeleteModalOpen}
				initialModalData={[feature]}
				editorSetting={deleteFeatureSetting}
				isAutoOpen={false}
			/>
			<tr>
				<td
					className="align-middle"
					style={{ whiteSpace: "wrap", wordWrap: "break-word" }}
				>
					<OverlayTrigger
						placement="top"
						overlay={
							<Tooltip>
								{code}
								{description ? ": " + description : ""}
							</Tooltip>
						}
					>
						<strong data-testid="test-pill">{name}</strong>
					</OverlayTrigger>
				</td>
				<td className="p-0" width="35px">
					<Button
						onClick={() => setIsModalOpen(true)}
						className="p-2"
						size="sm"
						variant="primary"
						style={{ border: "0px" }}
					>
						<FontAwesomeIcon icon={faPen} />
					</Button>
				</td>
				<td className="p-0" width="35px">
					<Button
						onClick={() => setIsDeleteModalOpen(true)}
						className="p-2"
						size="sm"
						variant="primary"
						style={{ border: "0px" }}
					>
						<FontAwesomeIcon icon={faMultiply} />
					</Button>
				</td>
			</tr>
		</>
	);
};

export const OfferingFeatures = ({ productId }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { data, isLoading, error } = useQuery(
		[Object.keys({ getFeatures })[0], productId],
		() =>
			getFeatures({
				productId,
			})
	);

	const queryClient = useQueryClient();
	const refreshFeatures = () => {
		queryClient.invalidateQueries({
			queryKey: [Object.keys({ getFeatures })[0], productId],
		});
	};

	const {
		mutateAsync: createFeatureM,
		isLoading: isLoadingNewFeature,
		isSuccess,
		reset: resetUsersQ,
	} = useMutation({
		mutationFn: createFeature,
		onSettled: refreshFeatures,
	});

	const createFeatureSetting = {
		action: modalActions.CREATE,
		fields: [
			[
				{
					field: "code",
					isMandatory: true,
					title: "Feature Code",
					placeholder: "Enter Feature Code",
				},
			],
			[
				{
					field: "name",
					isMandatory: true,
					title: "Name",
					placeholder: "Enter Name",
				},
			],
			[
				{
					field: "description",
					title: "Description",
					type: FieldType.TEXT_AREA,
					placeholder: "Enter Description",
				},
			],
			[
				{
					field: "defaultValue",
					title: "Default Value (On / Off)",
					type: FieldType.CHECKBOX,
				},
			],
		],
		buttonTitle: "Add Feature",
		formatModalTitle: () => `Add Feature`,
		submitButtonText: "Add Feature",
		apiFunctionSubmit: (data) => createFeatureM({ ...data, productId }),
	};

	return (
		<>
			<DataFormModal
				isModalOpen={isModalOpen}
				setIsModalOpen={setIsModalOpen}
				initialModalData={[{}]}
				editorSetting={createFeatureSetting}
				isAutoOpen={false}
			/>
			<Card
				className="mb-4 offeringUserCard"
				data-testid="test-offeringFeatures"
			>
				<BlockInput blocking={isLoading} />
				<Card.Header className="p-2 ps-3">
					<span className="mt-1">Features</span>
					<Button
						onClick={() => setIsModalOpen(true)}
						size="sm"
						className="p-1 float-end"
					>
						Add
					</Button>
				</Card.Header>
				{data && data.length ? (
					<Card.Body className="p-3">
						<Table responsive size="sm">
							<tbody>
								{data?.map((feature: FeatureDto) => (
									<FeatureRow
										key={feature.id}
										refreshFeatures={refreshFeatures}
										feature={feature}
									/>
								))}
							</tbody>
						</Table>
					</Card.Body>
				) : (
					<Card.Body className="p-3">No features</Card.Body>
				)}
			</Card>
		</>
	);
};
